import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/translations';
import MasonryGallery from '@/components/MasonryGallery';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Eventi = () => {
  const { language } = useLanguage();
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery?category=events`);
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      setGalleryImages([
        {
          id: '1',
          image_url: 'https://images.unsplash.com/photo-1772690445981-78b22eacda4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBldmVudCUyMHByb2Zlc3Npb25hbCUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc3OTgyMjk4OHww&ixlib=rb-4.1.0&q=85',
          alt_text: 'Corporate event'
        },
        {
          id: '2',
          image_url: 'https://images.unsplash.com/photo-1759560270562-468e8ba866e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBldmVudCUyMHByb2Zlc3Npb25hbCUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc3OTgyMjk4OHww&ixlib=rb-4.1.0&q=85',
          alt_text: 'Event awards'
        }
      ]);
    }
  };

  return (
    <div data-testid="eventi-page" className="pt-32">
      {/* Hero Section */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
            {language === 'it' ? 'Eventi & Aziende' : 'Events & Corporate'}
          </p>
          <h1
            className="text-5xl sm:text-6xl font-light tracking-tight leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="page-title"
          >
            {t('events.title', language)}
          </h1>
          <p className="text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto">
            {t('events.subtitle', language)}
          </p>
        </motion.div>
      </section>

      {/* Description Section */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="text-3xl sm:text-4xl font-light tracking-tight leading-tight mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {language === 'it' ? 'Professionalità e Visione' : 'Professionalism and Vision'}
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground mb-6">
              {t('events.description', language)}
            </p>
          </motion.div>
          <motion.img
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            src="https://static.prod-images.emergentagent.com/jobs/bcbecfbc-ec91-4a60-94e3-f60fea3e75c0/images/ceff6c63a46958aae4d35807a26501f0f2b1e8e07683052a4f63d66c0f13ce7b.jpeg"
            alt="Corporate Event"
            className="w-full h-96 object-cover rounded-sm"
          />
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-secondary/30 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl sm:text-4xl font-light tracking-tight leading-tight"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {language === 'it' ? 'Servizi' : 'Services'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: language === 'it' ? 'Eventi Aziendali' : 'Corporate Events', desc: language === 'it' ? 'Conferenze, convention, team building' : 'Conferences, conventions, team building' },
              { title: language === 'it' ? 'Musica & Concerti' : 'Music & Concerts', desc: language === 'it' ? 'Live, festival, eventi musicali' : 'Live shows, festivals, music events' },
              { title: 'Sport', desc: language === 'it' ? 'Gare, eventi sportivi, atleti' : 'Competitions, sports events, athletes' },
              { title: 'Branding', desc: language === 'it' ? 'Shooting aziendali, prodotti, team' : 'Corporate shoots, products, teams' }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="text-center p-6 bg-background/50 rounded-sm"
              >
                <h3
                  className="text-xl sm:text-2xl font-light mb-3"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {service.title}
                </h3>
                <p className="text-sm text-muted-foreground">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <h2
              className="text-3xl sm:text-4xl font-light tracking-tight leading-tight text-center"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {language === 'it' ? 'Portfolio' : 'Portfolio'}
            </h2>
          </motion.div>
          <MasonryGallery images={galleryImages} />
        </section>
      )}

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2
            className="text-4xl sm:text-5xl font-light tracking-tight leading-tight mb-8"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {language === 'it'
              ? 'Raccontiamo il vostro evento'
              : 'Let\'s tell your event story'}
          </h2>
          <Link to="/contatti">
            <Button size="lg" data-testid="cta-button-events">
              {t('events.cta', language)}
            </Button>
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default Eventi;
